'use client'

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'

import { useAccountVerification } from './account-verification'

type AccountValidationContextState = {
  shouldValidateAccountForm: () => boolean
  handleAccountFormOpen: () => void
  accountTrigger: () => Promise<boolean>
}

export const AccountValidationContext =
  createContext<AccountValidationContextState | null>(null)

type Props = {
  children: ReactNode
}

export const AccountValidationProvider = ({ children }: Props) => {
  const isAccountFormOpen = useRef(false)

  const { accountTrigger } = useAccountVerification()

  const shouldValidateAccountForm = useCallback(() => {
    return isAccountFormOpen.current
  }, [])

  const handleAccountFormOpen = useCallback(() => {
    isAccountFormOpen.current = true
  }, [])

  const value: AccountValidationContextState = useMemo(
    () => ({
      shouldValidateAccountForm,
      handleAccountFormOpen,
      accountTrigger,
    }),
    [shouldValidateAccountForm, handleAccountFormOpen, accountTrigger],
  )

  return (
    <AccountValidationContext.Provider value={value}>
      {children}
    </AccountValidationContext.Provider>
  )
}

export const useAccountValidation = () => {
  const context = useContext(AccountValidationContext)

  if (!context) {
    throw new Error(
      'useAccountValidation must be used within a AccountValidationProvider',
    )
  }

  return context
}
